import { 
  MdLightMode, 
  MdDarkMode,
  MdLanguage,
  MdExpandMore,
  MdPerson,
  MdLogout,
  MdCheck,
  MdMenu
} from 'react-icons/md';
import { useState, useEffect, useRef } from 'react';
import ertinuLogo from '../../assets/images/ertinu_logo.png';

import { useNavigate } from 'react-router-dom';
import AccountSettings from '../Settings/AccountSettings';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { BASE_URL } from '../../config/environment';

// Logo URL helper fonksiyonu
const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  // Eğer logoPath zaten tam URL ise (http ile başlıyorsa) direkt döndür
  if (logoPath.startsWith('http')) {
    return logoPath;
  }
  // Eğer relative path ise BASE_URL ile birleştir
  return `${BASE_URL}${logoPath}`;
};

const Header = ({ isSidebarOpen, onToggleSidebar, isMobile }) => {
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [user, setUser] = useState({
    name: '',
    initials: ''
  });
  const [company, setCompany] = useState({
    name: '',
    logo: null
  });
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);


  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      const fullName = userData.fullName || '';
      const initials = fullName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase();
      
      setUser({
        name: fullName,
        initials: initials
      });
    }

    // Dark mode tercihi yükle
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Firma bilgilerini getir
  const fetchCompanyInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await api.get('/me/company', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setCompany({
          name: response.data.company.name,
          logo: response.data.company.logo
        });
      }
    } catch (error) {
      console.error('Firma bilgileri getirme hatası:', error);
    }
  };

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  // Global refresh fonksiyonu - diğer component'lerden çağrılabilir
  useEffect(() => {
    window.refreshCompanyLogo = fetchCompanyInfo;
    return () => {
      delete window.refreshCompanyLogo;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'tr', label: t('common.turkish') },
    { code: 'en', label: t('common.english') }
  ];

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  return (
    <header className="h-[90px] bg-[#272c39] text-white border-b border-white/20 shadow-[0_2px_8px_rgba(0,0,0,0.15)] relative z-20">
      <div className="h-full px-3 sm:px-6 flex items-center justify-between">
        <div className="h-full flex items-center gap-2 sm:gap-4">
          {/* Hamburger Menu - Sadece mobilde görünür */}
          {isMobile && (
            <button
              onClick={onToggleSidebar}
              className="p-2 hover:bg-white/10 rounded-md lg:hidden"
              aria-label="Toggle Menu"
            >
              <MdMenu size={24} className="text-white" />
            </button>
          )}
          
          <div className="flex items-center">
            {company.logo ? (
              <img 
                src={getLogoUrl(company.logo)}
                alt={`${company.name} Logo`}
                className="w-[60px] sm:w-[90px] h-[42px] sm:h-[63px] object-contain"
                onError={(e) => {
                  // Logo yüklenemezse varsayılan logoyu göster
                  e.target.src = ertinuLogo;
                  e.target.alt = "ERTINU Logo";
                }}
              />
            ) : (
              <img 
                src={ertinuLogo} 
                alt="ERTINU Logo" 
                className="w-[60px] sm:w-[90px] h-[42px] sm:h-[63px] object-contain"
              />
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Theme toggle */}
          <button 
            onClick={toggleDarkMode}
            className="p-1.5 hover:bg-white/10 rounded-full"
          >
            {isDarkMode ? (
              <MdLightMode size={20} className="text-white/90" />
            ) : (
              <MdDarkMode size={20} className="text-white/90" />
            )}
          </button>
          
          {/* Kullanıcı menüsü */}
          <div className="relative" ref={userMenuRef}>
            <button 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 hover:bg-white/10 rounded-md px-2 py-1.5"
            >
              <span className="text-sm text-white/90">{user.name}</span>
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-sm font-medium">{user.initials}</span>
              </div>
            </button>

            {/* Dropdown menü */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 min-w-[200px] border border-gray-200 dark:border-gray-700">
                {/* Account Settings */}
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    setShowAccountSettings(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <MdPerson size={18} />
                  {t('settings.accountSettings')}
                </button>

                {/* Language Selector */}
                <div className="relative">
                  <button
                    onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <MdLanguage size={18} />
                      {t('common.language')}
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{currentLanguage.toUpperCase()}</span>
                      <MdExpandMore 
                        className={`transition-transform duration-200 ${
                          isLanguageOpen ? 'rotate-180' : ''
                        }`}
                        size={18}
                      />
                    </div>
                  </button>

                  {/* Language Dropdown */}
                  {isLanguageOpen && (
                    <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 min-w-[160px] border border-gray-200 dark:border-gray-700">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setIsLanguageOpen(false);
                            setIsUserMenuOpen(false);
                            handleLanguageChange(lang.code);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                        >
                          {lang.label}
                          {currentLanguage === lang.code && (
                            <MdCheck size={18} className="text-blue-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <MdLogout size={18} />
                  {t('navigation.logOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showAccountSettings && (
        <AccountSettings 
          onClose={() => setShowAccountSettings(false)} 
          userId={JSON.parse(localStorage.getItem('user')).id}
        />
      )}


    </header>
  );
};

export default Header; 